import { useState, useEffect } from 'react'
import { fetchMonthlySummary, fetchGoals } from '../utils/api'
import type { MonthlyGoalSummary } from '../types/okr'

export default function MonthlySummary() {
  const [month, setMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [summaries, setSummaries] = useState<MonthlyGoalSummary[]>([])
  const [goals, setGoals] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [month])

  const loadData = async () => {
    try {
      setLoading(true)
      const [summaryData, goalsData] = await Promise.all([
        fetchMonthlySummary(month),
        fetchGoals()
      ])
      setSummaries(summaryData)
      setGoals(goalsData)
    } catch (err) {
      setError('加载月度汇总数据失败')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // 计算总体统计
  const totalUpdates = summaries.reduce((sum, s) => sum + s.totalUpdates, 0)
  const avgCompletionRate = summaries.length > 0 
    ? summaries.reduce((sum, s) => sum + s.completionRate, 0) / summaries.length 
    : 0

  // 热力图数据（模拟）
  const heatmapData = Array.from({ length: 4 }, (_, _weekIndex) => 
    Array.from({ length: 7 }, (_, _dayIndex) => 
      Math.floor(Math.random() * 10) // 随机数据，实际应来自后端
    )
  )

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg">加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600">{error}</div>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">
          重试
        </button>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">📈 月度目标完成度概览</h1>
          <div className="flex items-center gap-4">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            />
            <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50">
              导出报表
            </button>
          </div>
        </div>

        {/* 总体进度 */}
        <div className="bg-white border rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">总体进度</h2>
              <div className="text-3xl font-bold mt-2">{avgCompletionRate.toFixed(1)}%</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">总更新次数</div>
              <div className="text-2xl font-bold">{totalUpdates}</div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-blue-600 h-4 rounded-full transition-all duration-500"
              style={{ width: `${avgCompletionRate}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 左侧：热力图和趋势图 */}
          <div className="space-y-8">
            {/* 热力图 */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold mb-4">进展热力图</h3>
              <div className="flex items-center gap-2 mb-4">
                <div className="text-sm text-gray-600">低</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div 
                      key={level}
                      className="w-6 h-6 rounded"
                      style={{ 
                        backgroundColor: `rgba(59, 130, 246, ${level * 0.2})`,
                        border: '1px solid rgba(59, 130, 246, 0.1)'
                      }}
                    />
                  ))}
                </div>
                <div className="text-sm text-gray-600">高</div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-sm font-medium text-gray-600 text-left py-2">周次</th>
                      {['一', '二', '三', '四', '五', '六', '日'].map(day => (
                        <th key={day} className="text-sm font-medium text-gray-600 text-center py-2">{day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData.map((week, weekIndex) => (
                      <tr key={weekIndex}>
                        <td className="text-sm text-gray-600 py-2">第{10 + weekIndex}周</td>
                        {week.map((value, dayIndex) => (
                          <td key={dayIndex} className="text-center py-2">
                            <div 
                              className="w-8 h-8 mx-auto rounded flex items-center justify-center text-xs"
                              style={{ 
                                backgroundColor: `rgba(59, 130, 246, ${value / 10})`,
                                border: '1px solid rgba(59, 130, 246, 0.1)'
                              }}
                            >
                              {value > 0 ? value : ''}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 进度趋势图 */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold mb-4">进度趋势</h3>
              <div className="relative h-64">
                {/* 简化版趋势图 - 实际应用应使用图表库 */}
                <div className="absolute inset-0 flex items-end">
                  {summaries.slice(0, 5).map((summary, _idx) => {
                    const progressHistory = summary.progressHistory
                    if (progressHistory.length === 0) return null
                    
                    const lastProgress = progressHistory[progressHistory.length - 1].progress
                    return (
                      <div key={summary.goalId} className="flex-1 flex flex-col items-center px-2">
                        <div className="text-xs text-gray-500 mb-1">
                          {summary.goalTitle.substring(0, 8)}...
                        </div>
                        <div 
                          className="w-3/4 bg-blue-500 rounded-t"
                          style={{ height: `${lastProgress}%` }}
                        />
                        <div className="text-xs mt-1">{lastProgress}%</div>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="text-sm text-gray-600 mt-4 text-center">
                展示各目标月度进度变化趋势
              </div>
            </div>
          </div>

          {/* 右侧：关键里程碑和目标详情 */}
          <div className="space-y-8">
            {/* 关键里程碑 */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold mb-4">关键里程碑</h3>
              <div className="space-y-4">
                {summaries.flatMap(summary => 
                  summary.keyMilestones.map((milestone, idx) => (
                    <div key={`${summary.goalId}-${idx}`} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600">🏆</span>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{summary.goalTitle}</div>
                        <div className="text-sm text-gray-600 mt-1">{milestone.content}</div>
                        <div className="text-xs text-gray-500 mt-2">
                          {new Date(milestone.date).toLocaleDateString('zh-CN')} {milestone.timestamp.slice(11, 16)}
                        </div>
                      </div>
                    </div>
                  ))
                ).slice(0, 5)}
                
                {summaries.flatMap(s => s.keyMilestones).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    本月暂无关键里程碑
                  </div>
                )}
              </div>
            </div>

            {/* 月度目标详情 */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold mb-4">月度目标详情</h3>
              <div className="space-y-4">
                {summaries.map(summary => {
                  const goal = goals.find(g => g.id === summary.goalId)
                  return (
                    <div key={summary.goalId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">{summary.goalTitle}</div>
                        <div className="text-lg font-bold">{summary.completionRate}%</div>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${summary.completionRate}%` }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">更新次数</div>
                          <div className="font-medium">{summary.totalUpdates}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">负责人</div>
                          <div className="font-medium">{goal?.owner || '未知'}</div>
                        </div>
                      </div>
                      
                      {summary.progressHistory.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="text-sm text-gray-600 mb-2">进度历史</div>
                          <div className="flex items-end gap-1 h-12">
                            {summary.progressHistory.slice(-7).map((item, idx) => (
                              <div key={idx} className="flex-1 flex flex-col items-center">
                                <div 
                                  className="w-3/4 bg-blue-200 rounded-t"
                                  style={{ height: `${item.progress}%` }}
                                />
                                <div className="text-xs text-gray-500 mt-1">
                                  {item.date.split('-').pop()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                
                {summaries.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    本月暂无目标数据
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}